import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Button, notification, Table, Tag, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import numeral from 'numeral';
import { HomeOutlined } from '@ant-design/icons';
import useErrorMessage from '../../utils/ErrorMessage';
import { quittanceStatuses } from '../../utils/constants/tagColors';
import MercanetForm from './MercanetForm';
import useAuthContext from '../../contexts/AuthContext';

const PaperworkFeePaymentModal = ({
  paymentModal,
  setIsLoading,
  isLoading,
  paperworkFee,
  setPaymentModal,
  contract
}) => {
  const { t } = useTranslation();
  const { Column } = Table;
  const navigate = useNavigate();
  const { message } = useErrorMessage();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paymentStatus = params.get('paymentStatus');
  const { dispatchAPI } = useAuthContext();
  const [seal, setSeal] = useState(null);
  const [formattedData, setFormattedData] = useState();

  const getFullData = () => {
    try {
      setFormattedData([
        {
          period_date:
            paperworkFee[0].period_date || contract?.quittances[0]?.period_date,
          paperworkFee_status:
            paperworkFee[0].status || contract?.quittances[0]?.quittance_status,
          amount:
            paperworkFee[0].amount +
            (contract.payment_type === 'CARD'
              ? contract?.quittances[0]?.selling_price_customer
              : 0)
        }
      ]);
    } catch (e) {
      message(e);
    }
  };

  const getSeal = async () => {
    try {
      const { data } = await dispatchAPI('POST', {
        url: 'mercanet-payments/seal',
        body: {
          ...(contract.payment_type === 'CARD'
            ? {
                selectedQuittances: contract?.quittances[0]?._id
              }
            : { selectedPaperWorkFee: paperworkFee[0]._id }),
          origin: window.location.href.split('?')[0]
        }
      });
      setSeal(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    if (paymentModal) {
      (() => {
        setIsLoading(true);
        getFullData();
        setIsLoading(false);
      })();
    }
    // eslint-disable-next-line
  }, [paymentModal]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (formattedData) await getSeal();
      setIsLoading(false);
    })();
    // eslint-disable-next-line
  }, [formattedData]);

  useEffect(() => {
    if (paymentStatus === '1')
      notification.success({
        message: 'Paiement réussi',
        key: 'successPayment',
        duration: 10
      });
  }, [paymentStatus]);

  return (
    <>
      <Modal
        title={
          <p>
            {contract?.payment_type === 'CARD'
              ? t('paperWorkFee.modal.title_card')
              : ''}
            &nbsp;
            {paperworkFee.length && contract?.payment_type === 'CARD'
              ? 'AVEC LE'
              : ''}
            &nbsp;
            {paperworkFee.length ? t('paperWorkFee.modal.title') : ''}
          </p>
        }
        centered
        open={paymentModal}
        onCancel={() => {
          setPaymentModal(false);
        }}
        width={600}
        footer={[
          <div style={{ textAlign: 'center', fontWeight: 700 }}>
            {t('paperWorkFee.modal.modal_obligation')}
          </div>
        ]}
      >
        {paperworkFee.length ? (
          <div style={{ textAlign: 'center', fontWeight: 700 }}>
            {t('paperWorkFee.modal.modal_succes')}
          </div>
        ) : (
          []
        )}
        <div
          style={{
            marginBottom: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Divider>Frais à payer</Divider>
          <Table dataSource={formattedData} pagination={false}>
            <Column
              title={t('paperWorkFee.form.period_date')}
              dataIndex="period_date"
              render={(date) => date && moment(date).format('MM/YYYY')}
            />
            <Column
              title={t('paperWorkFee.form.paperworkFee_status')}
              dataIndex="paperworkFee_status"
              render={(paperworkFee_status) => (
                <Tag color={quittanceStatuses[paperworkFee_status]}>
                  {t(`quittances.tags.${paperworkFee_status}`)}
                </Tag>
              )}
            />
            <Column
              title={t('paperWorkFee.form.amount')}
              dataIndex="amount"
              render={(p) => `${numeral(p || 0).format('0,0.00')}€`}
            />
          </Table>
          <Divider>||</Divider>
          <MercanetForm seal={seal} isLoading={isLoading} />
          <span
            style={{
              textAlign: 'center',
              fontWeight: 700,
              marginBottom: 25,
              marginTop: 25
            }}
          >
            OU
          </span>
          <Button
            onClick={() => navigate('/')}
            type="primary"
            style={{
              width: 230,
              padding: '4px 15px'
            }}
          >
            <span
              style={{
                display: 'inline'
              }}
            >
              {t('paperWorkFee.modal.button_home')}
            </span>
            <HomeOutlined />
          </Button>
        </div>
      </Modal>
    </>
  );
};

PaperworkFeePaymentModal.propTypes = {
  setPaymentModal: PropTypes.func.isRequired,
  paymentModal: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  paperworkFee: PropTypes.shape({
    amount: PropTypes.string,
    _id: PropTypes.string,
    length: PropTypes.number
  }).isRequired,
  contract: PropTypes.shape({
    payment_type: PropTypes.string,
    quittances: PropTypes.shape({
      period_date: PropTypes.instanceOf(Date),
      quittance_status: PropTypes.string,
      selling_price_customer: PropTypes.number
    }).isRequired
  }).isRequired
};

export default PaperworkFeePaymentModal;
